import { useMemo, useState } from 'react';

import { InviteAppShortcutsCookieCache } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/cache';
import {
	useInvitedAppShortcuts as useInvitedAppShortcutsPlatform,
	type UseInvitedAppShortcutsResult,
} from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';
import { Cookies } from '@confluence/cookies';
import { fg } from '@confluence/feature-gating';
import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import {
	stringifyPersonalizationAttributes,
	type PersonalizationAttribute,
	type StringifiedPersonalizationAttribute,
} from '@confluence/legacy-schema-bridge/entry-points/stringifyPersonalizationAttributes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useLicensedProducts } from './useLicensedProducts.tsx';

const targetProductsJira = ['jira'] as const;
const targetProductsEmpty = [];

type InvitedAppShortcutsSiteUserPersonalizationVariables = {
	cloudId: string;
	userId: string;
};

type InvitedAppShortcutsSiteUserPersonalizationData = {
	experimentalSiteUserPersonalization: {
		attributes: StringifiedPersonalizationAttribute[] | null;
	};
};

type InvitedAppShortcutsSiteUserPersonalizationResult = {
	data: InvitedAppShortcutsSiteUserPersonalizationData | undefined;
	loading: boolean;
	error: ApolloErrorInterop | undefined;
};

const useInvitedAppShortcutsSiteUserTraits = ({
	variables,
	skip,
}: {
	variables: InvitedAppShortcutsSiteUserPersonalizationVariables;
	skip?: boolean;
}): InvitedAppShortcutsSiteUserPersonalizationResult => {
	return useExperimentalQuery<
		InvitedAppShortcutsSiteUserPersonalizationData,
		InvitedAppShortcutsSiteUserPersonalizationVariables,
		{ attributes: PersonalizationAttribute[] | null }
	>({
		/* eslint-disable confluence-targeting-usage/no-legacy-tap-personalization-api -- Required while invited app shortcuts still depend on Jira-prefixed TAP traits filtered from useSiteUserTraits SSR inputs. See CCCEMAU-2527. */
		url: ({ cloudId, userId }) =>
			`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${userId}`,
		/* eslint-enable confluence-targeting-usage/no-legacy-tap-personalization-api */
		method: 'GET',
		variables,
		parseResult: (json): InvitedAppShortcutsSiteUserPersonalizationData => ({
			experimentalSiteUserPersonalization: {
				attributes: stringifyPersonalizationAttributes(json.attributes),
			},
		}),
		skip,
	});
};

export const useResolveInvitedAppShortcuts = (): UseInvitedAppShortcutsResult => {
	const { cloudId, userId } = useSessionData();
	const { hasJira } = useLicensedProducts();
	const [cacheNeedsRefresh, setCacheNeedsRefresh] = useState(false);

	const userTraits = useInvitedAppShortcutsSiteUserTraits({
		skip: !(fg('twcg-460-personalised-invite-notifications-m2-gate') && cacheNeedsRefresh),
		variables: {
			cloudId,
			userId: userId || '',
		},
	});

	return useInvitedAppShortcutsPlatform(
		useMemo(
			() =>
				fg('twcg-460-personalised-invite-notifications-m2-gate')
					? {
							enabled: true,
							targetProducts: hasJira ? targetProductsJira : targetProductsEmpty,
							sourceProduct: 'confluence',
							cloudId,
							userId,
							traits: userTraits.data?.experimentalSiteUserPersonalization?.attributes ?? null,
							cache: getInvitedAppShortcutsCache(),
							onCacheNeedsRefresh: () => {
								setCacheNeedsRefresh(true);
							},
						}
					: { enabled: false },
			[userTraits, cloudId, hasJira, userId],
		),
	);
};

let cache: InviteAppShortcutsCookieCache | undefined;

export const getInvitedAppShortcutsCache = (): InviteAppShortcutsCookieCache => {
	if (!cache) {
		cache = new InviteAppShortcutsCookieCache({
			setCookie: Cookies.set,
			getCookie: Cookies.get,
		});
	}
	return cache;
};
